import { RefObject } from 'react';
import { useOrderTablePreference } from '@/pages/orders/_hooks/useOrderTablePreference';

import {
  checkboxColumn,
  marketColumn,
  orderDateColumn,
  ordererColumn,
  orderNoColumn,
  orderStatus,
  productNameColumn,
  productNoColumn,
  productOptionColumn,
  quantityColumn,
  salesAmountColumn,
} from '../orderTableColumnBlocks';

import EmptyFaceIcon from '@/assets/icons/EmptyFaceIcon.svg?react';
import { TooltipProvider } from '@/components/atoms/PcTooltip';
import { LayoutOrderColumn } from '@/pages/orders/_layouts/LayoutOrderColumn';
import { OrderItem } from '@/types/order-list';
import { deepEqual } from '@/utils/deep-equal';
import { InfiniteScrollSentinel } from '../../../common/InfiniteScrollSentinel';
import MarketTable from '../../../common/MarketTable';
import { useOrderDetails } from '../../OrderDetailsProvider';

interface Props {
  orderList: OrderItem[];
  listEndSentinelRef: RefObject<HTMLDivElement>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export function OrderTableColumn({
  orderList,
  listEndSentinelRef,
  hasNextPage,
  isFetchingNextPage,
}: Props) {
  const { checkedOrders, setCheckedOrders } = useOrderDetails();

  const handleCheck = (order: OrderItem, checked: boolean) => {
    setCheckedOrders((prev) =>
      checked ? [...prev, order] : prev.filter((item) => !deepEqual(item, order)),
    );
  };

  const selectableOrders = orderList.filter((order) => order.marketInfo.isOrderFurtherActionEnable);

  const allChecked =
    selectableOrders.length > 0 && checkedOrders.length === selectableOrders.length;

  const handleCheckAll = (checked: boolean) => {
    if (selectableOrders.length === 0) return; // 선택 가능한 주문이 없으면 아무 작업도 하지 않음
    setCheckedOrders(checked ? selectableOrders : []); // ✅ 전체 Order 객체 저장
  };

  const columns = [
    orderNoColumn,
    ...(selectableOrders.length > 0
      ? [checkboxColumn({ allChecked, handleCheckAll, checkedOrders, handleCheck })]
      : []),
    orderStatus,
    marketColumn,
    orderDateColumn,
    productNameColumn,
    productOptionColumn,
    salesAmountColumn,
    quantityColumn,
    productNoColumn({ showSellerPageButton: true }),
    ordererColumn,
  ];

  const columnIds = columns.map((column) => column.id as string);
  const { columnOrder, setColumnOrder, columnSizing, setColumnSizing } = useOrderTablePreference({
    storageKey: 'cancel',
    columnIds,
  });

  return (
    <TooltipProvider>
      <LayoutOrderColumn className="relative">
        <MarketTable
          orderList={orderList}
          columns={columns}
          columnOrder={columnOrder}
          setColumnOrder={setColumnOrder}
          columnSizing={columnSizing}
          onColumnSizingChange={setColumnSizing}
          enableColumnResize
          enableRowClickSelect
          bodyFooter={
            <InfiniteScrollSentinel
              listEndSentinelRef={listEndSentinelRef}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
            />
          }
        />
        {orderList.length === 0 && (
          <div className="absolute inset-0 top-[40px] z-[20] flex flex-col items-center justify-center gap-[20px] rounded-[20px] bg-white">
            <EmptyFaceIcon />
            <p className="text-gr-500">검색결과가 없어요.</p>
          </div>
        )}
      </LayoutOrderColumn>
    </TooltipProvider>
  );
}

export default OrderTableColumn;
